// frontend/src/components/TripManagement.js

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import TripSelector from './TripSelector';
import TripTracker from './TripTracker';
import SurveyDisplay from './SurveyDisplay';
import axios from '../axiosConfig';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import WebSocketEvents from '../services/WebSocketEvents';
import { getTripEntityId, isTripEntityMismatch } from '../utils/entityMatch';
import { isDriver as checkIsDriver } from '../utils/roleUtils';

const CHILD_OWNED_START_ERROR_CODES = new Set([
  'GPS_SHARING_OFF',
  'GPS_FEED_INACTIVE',
  'GPS_FEED_STATE_UNAVAILABLE',
  'GPS_SOURCE_REQUIRED',
  'GPS_SOURCE_UNAVAILABLE',
  'GPS_DEVICE_UNAVAILABLE',
  'GPS_DEFAULT_DEVICE_UNAVAILABLE',
  'GPS_SOURCE_VEHICLE_REQUIRED',
  'GPS_SOURCE_VEHICLE_INVALID',
  'GPS_DEVICE_TRIP_CONFLICT'
]);

const TripManagement = ({ currentEntity }) => {
  const location = useLocation();
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showSurvey, setShowSurvey] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [completingTripId, setCompletingTripId] = useState(null);
  const completeTripInFlightRef = useRef(null);
  const userId = parseInt(localStorage.getItem('userId'), 10);
  const selectedTripEntityId = getTripEntityId(selectedTrip);
  const selectedTripMatchesCurrentEntity = Boolean(
    selectedTrip &&
    currentEntity?.entityId &&
    selectedTripEntityId &&
    selectedTripEntityId.toString() === currentEntity.entityId.toString()
  );
  const scopedSelectedTrip = selectedTripMatchesCurrentEntity ? selectedTrip : null;

  // Auto-select trip if tripId is passed via navigation state (from DriverDashboard)
  useEffect(() => {
    const fetchAndSelectTrip = async () => {
      const tripId = location.state?.tripId;
      if (tripId && currentEntity?.entityId && !scopedSelectedTrip) {
        try {
          const response = await axios.get(`/api/trips/${currentEntity.entityId}/${tripId}`);
          if (response.data?.success && response.data?.data) {
            const resolvedEntityId =
              response.data.data.entity_id ??
              response.data.data.entityId ??
              currentEntity.entityId;
            setSelectedTrip({
              ...response.data.data,
              entity_id: response.data.data.entity_id ?? resolvedEntityId,
              entityId: response.data.data.entityId ?? resolvedEntityId,
            });
          }
        } catch (error) {
          console.error('Error fetching trip:', error);
          toast.error(t('errorFetchingTrip'));
        }
      }
    };
    fetchAndSelectTrip();
  }, [location.state?.tripId, currentEntity?.entityId, scopedSelectedTrip]);

  // Re-fetch trip data when page becomes visible after being hidden.
  // This handles the case where the trip status changed while the rider was
  // away (e.g., driver started the trip while rider had the page backgrounded).
  useEffect(() => {
    if (!scopedSelectedTrip?.id || !currentEntity?.entityId) return;

    let lastHiddenTime = null;
    const REFRESH_THRESHOLD_MS = 10000; // Only refresh if hidden for >10 seconds

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenTime = Date.now();
      } else if (document.visibilityState === 'visible' && lastHiddenTime) {
        const hiddenDuration = Date.now() - lastHiddenTime;
        lastHiddenTime = null;

        if (hiddenDuration > REFRESH_THRESHOLD_MS) {
          console.log('[TripManagement] Page visible after backgrounding, refreshing trip data', {
            tripId: scopedSelectedTrip.id,
            hiddenDurationMs: hiddenDuration
          });
          try {
            const response = await axios.get(`/api/trips/${currentEntity.entityId}/${scopedSelectedTrip.id}`);
            if (response.data?.success && response.data?.data) {
              setSelectedTrip(prev => ({
                ...prev,
                ...response.data.data
              }));
            }
          } catch (error) {
            console.error('[TripManagement] Error refreshing trip data after visibility change:', error);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [scopedSelectedTrip?.id, currentEntity?.entityId]);

  // Listen for global WebSocket status updates
  useEffect(() => {
    const handleStatusUpdate = (data) => {
      console.log('TripManagement received status update:', data);
      
      // If this is the currently selected trip, update its status
      if (scopedSelectedTrip && 
          scopedSelectedTrip.id.toString() === data.tripId.toString() &&
          currentEntity?.entityId.toString() === data.entityId.toString()) {
        
        console.log(`Updating selected trip ${scopedSelectedTrip.id} status to ${data.status} (via WebSocket)`);
        
        setSelectedTrip(prev => ({
          ...prev,
          status: data.status
        }));
        
        // Notify TripSelector to refresh its list - dispatch a custom event
        window.dispatchEvent(new CustomEvent('trip-status-changed', {
          detail: { tripId: data.tripId, status: data.status }
        }));
      }
    };
    
    WebSocketEvents.on('status_update', handleStatusUpdate);

    return () => {
      WebSocketEvents.removeListener('status_update', handleStatusUpdate);
    };
  }, [scopedSelectedTrip, currentEntity]);

  // Listen for survey reminder triggers
  useEffect(() => {
    const handleSurveyReminderTrigger = async (data) => {
      console.log('TripManagement received survey reminder trigger:', data);

      const { tripId, entityId, eligibleRiderIds } = data;

      // Only trigger survey if logged-in user is in eligible list
      if (eligibleRiderIds.includes(userId)) {
        console.log('User is eligible for survey reminder', { userId });

        try {
          const surveyResponse = await axios.post(
            `/api/surveys/${entityId}/check-trigger`,
            {
              tripId,
              triggerType: 'trip_completion'
            }
          );

          if (surveyResponse.data.success &&
              surveyResponse.data.data &&
              surveyResponse.data.data.length > 0) {

            const surveyToShow = surveyResponse.data.data[0];
            setSurvey(surveyToShow);

            // Show survey after display delay
            setTimeout(() => {
              setShowSurvey(true);
            }, (surveyToShow.displayDelay || 5) * 1000);
          }
        } catch (surveyError) {
          console.log('Survey check failed:', surveyError);
        }
      } else {
        console.log('User not eligible for survey reminder', { userId });
      }
    };

    WebSocketEvents.on('survey_reminder_trigger', handleSurveyReminderTrigger);

    return () => {
      WebSocketEvents.removeListener('survey_reminder_trigger', handleSurveyReminderTrigger);
    };
  }, [userId, currentEntity]);

  // Memoize callback functions
  const handleTripSelect = useCallback((trip) => {
    if (!trip) {
      setSelectedTrip(null);
      return;
    }
    if (isTripEntityMismatch(trip, currentEntity)) {
      console.warn('[TripManagement] Ignoring trip selected for a different entity', {
        tripId: trip.id,
        tripEntityId: getTripEntityId(trip),
        currentEntityId: currentEntity?.entityId
      });
      setSelectedTrip(null);
      return;
    }
    const resolvedEntityId = trip.entity_id ?? trip.entityId ?? currentEntity?.entityId;
    setSelectedTrip({
      ...trip,
      entity_id: trip.entity_id ?? resolvedEntityId,
      entityId: trip.entityId ?? resolvedEntityId,
    });
  }, [currentEntity]);

  const handleStartTrip = useCallback(async (tripId, archivedType, startOptions = {}) => {
    try {
      const payload = {};
      if (archivedType !== undefined && archivedType !== null) {
        payload.archived_type = archivedType;
      }
      if (startOptions.vehicleId !== undefined && startOptions.vehicleId !== null) {
        payload.vehicle_id = startOptions.vehicleId;
      }
      if (startOptions.overrideVehicleConflict === true) {
        payload.override_vehicle_conflict = true;
      }
      const response = await axios.post(`/api/trips/${currentEntity.entityId}/${tripId}/start`, payload);
      const resolvedArchivedType = response?.data?.selected_archived_type ?? response?.data?.archived_type ?? archivedType ?? null;
      toast.success(t('Trip started successfully'));
      
      // Update UI immediately for better user experience
      // The WebSocket update will eventually arrive to confirm
      setSelectedTrip((prev) => ({
        ...prev,
        status: 'in_progress',
        selected_archived_type: resolvedArchivedType
      }));

      return resolvedArchivedType;
    } catch (error) {
      const recoveryCode = error?.response?.data?.code;
      const isStartRecoveryPrompt = recoveryCode === 'VEHICLE_REQUIRED'
        || recoveryCode === 'VEHICLE_ACTIVE_TRIP_CONFLICT';
      const isChildOwnedStartError = CHILD_OWNED_START_ERROR_CODES.has(recoveryCode);
      if (!isStartRecoveryPrompt && !isChildOwnedStartError) {
        console.error('Failed to start trip:', error);
        toast.error(t('Failed to start trip'));
      }
      throw error;
    }
  }, [currentEntity]);

  // Solution 3: Trip completion retry/backoff
  // See: backend/docs/2026-01-12-GPS-TRACKING-RELIABILITY-IMPROVEMENTS.md
  const completeTripRef = useRef(null);

  const handleCompleteTrip = useCallback(async (tripId, attempt = 1) => {
    const MAX_ATTEMPTS = 3;
    const BACKOFF_BASE_MS = 1000; // 1 second base, doubles each attempt
    const normalizedTripId = tripId?.toString?.() || String(tripId);
    const isInitialAttempt = attempt === 1;

    if (isInitialAttempt) {
      if (completeTripInFlightRef.current) {
        return { ignored: true };
      }
      completeTripInFlightRef.current = normalizedTripId;
      setCompletingTripId(tripId);
    }

    try {
      const response = await axios.post(`/api/trips/${currentEntity.entityId}/${tripId}/complete`);
      toast.success(t('Trip completed successfully'));

      // Update UI immediately
      setSelectedTrip((prev) => ({
        ...prev,
        status: 'completed',
      }));

      // Note: Survey reminders are now triggered via WebSocket
      // See survey_reminder_trigger handler below
      return response?.data;
    } catch (error) {
      // Determine if error is retryable (network errors, timeouts, 5xx server errors)
      const isRetryable = !error.response ||
                          error.response.status >= 500 ||
                          error.code === 'ECONNABORTED' ||
                          error.code === 'ERR_NETWORK';

      if (isRetryable && attempt < MAX_ATTEMPTS) {
        const backoffMs = BACKOFF_BASE_MS * Math.pow(2, attempt - 1);
        console.log('Trip completion failed, retrying...', {
          tripId,
          attempt,
          maxAttempts: MAX_ATTEMPTS,
          backoffMs,
          errorCode: error.code,
          status: error.response?.status
        });
        toast.info(t('connectionIssueRetrying'), { autoClose: 2000 });

        // Wait for backoff period then retry
        await new Promise(resolve => setTimeout(resolve, backoffMs));
        return completeTripRef.current(tripId, attempt + 1);
      }

      // Non-retryable error or max attempts reached
      console.error('Failed to complete trip:', error, {
        tripId,
        attempt,
        maxAttempts: MAX_ATTEMPTS
      });
      toast.error(t('Failed to complete trip'));
      throw error;
    } finally {
      if (isInitialAttempt) {
        completeTripInFlightRef.current = null;
        setCompletingTripId((current) => (
          current?.toString?.() === normalizedTripId ? null : current
        ));
      }
    }
  }, [currentEntity]);

  // Keep ref in sync for recursive calls
  useEffect(() => {
    completeTripRef.current = handleCompleteTrip;
  }, [handleCompleteTrip]);

  const handleCancelTrip = useCallback(async (tripId, reason) => {
    try {
      await axios.post(`/api/trips/${currentEntity.entityId}/${tripId}/cancel`, { reason });
      toast.success(t('Trip canceled successfully'));
      
      // Update UI immediately
      setSelectedTrip((prev) => ({
        ...prev,
        status: 'canceled',
      }));

    } catch (error) {
      console.error('Failed to cancel trip:', error);
      toast.error(t('Failed to cancel trip'));
      throw error;
    }
  }, [currentEntity]);

  // This function is called by TripTracker when a WebSocket status update is received
  const handleTripStatusChange = useCallback((tripId, newStatus, isFromWebSocket = false, archivedType = null) => {
    console.log(`TripManagement: Updating trip ${tripId} status to ${newStatus} (from WebSocket: ${isFromWebSocket})`);
    
    setSelectedTrip((prev) => {
      if (!prev || prev.id.toString() !== tripId.toString()) {
        return prev;
      }
      const nextState = { ...prev, status: newStatus };
      if (archivedType) {
        nextState.selected_archived_type = archivedType;
      }
      return nextState;
    });

    if (newStatus === 'completed' || newStatus === 'canceled') {
      setCompletingTripId((current) => (
        current?.toString?.() === tripId.toString() ? null : current
      ));
      if (completeTripInFlightRef.current === tripId.toString()) {
        completeTripInFlightRef.current = null;
      }
    }
    
  }, []);

  // Memoize isDriverRole value
  const isDriverRole = useMemo(() =>
    checkIsDriver(currentEntity?.userRole),
    [currentEntity?.userRole]
  );

  return (
    <div className="space-y-4 pt-4">
      <div className="w-full max-w-4xl mx-auto">
        <TripSelector
          userId={userId}
          currentEntity={currentEntity}
          onSelectTrip={handleTripSelect}
          selectedTripId={scopedSelectedTrip?.id}
          selectedTrip={scopedSelectedTrip}
        />
      </div>
      {scopedSelectedTrip && (
        <div className="w-full max-w-4xl mx-auto">
          <TripTracker
            tripId={scopedSelectedTrip.id}
            tripData={scopedSelectedTrip}
            onStartTrip={handleStartTrip}
            onCompleteTrip={handleCompleteTrip}
            onCancelTrip={handleCancelTrip}
            onTripStatusChange={handleTripStatusChange}
            currentEntity={currentEntity}
            isDriver={isDriverRole}
            isCompletingTrip={completingTripId?.toString?.() === scopedSelectedTrip.id.toString()}
          />
        </div>
      )}
      
      {/* Survey Display Modal */}
      {showSurvey && survey && (
        <SurveyDisplay
          survey={survey}
          entityId={currentEntity.entityId}
          tripId={scopedSelectedTrip?.id}
          onClose={() => {
            setShowSurvey(false);
            setSurvey(null);
          }}
          onComplete={() => {
            console.log('Survey completed for trip:', selectedTrip?.id);
            setShowSurvey(false);
            setSurvey(null);
          }}
        />
      )}
    </div>
  );
};

export default TripManagement;
